import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './PayoutSplitAllocator.sass'
import {
  useCustomSelectStateFromItemSource,
  CustomSelect,
  SingleItemSelectedDefault,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import { CursorPageQuery } from 'types/definitions'
import Avatar from 'components/Avatar'
import { PayoutSplitAllocatorViewModel } from './PayoutSplitAllocatorViewModel'
import Button from 'components/Button'
import { formatMoney } from 'utils/money-util'
import NumericInput from 'components/inputs/NumericInput'
import { CancellationToken } from 'infra/cancellation'
import { AllocationInputViewModel } from './AllocationInputViewModel'
import {
  FormRow,
  FormColumns,
  FormColumn,
  FormGroup,
  Help,
} from '../../../components/inputs'
import { getFeatureToggleClient } from '../../../infra/featureToggles'

interface PayoutSplitAllocatorProps {
  vm: PayoutSplitAllocatorViewModel
}

/**
 * The PayoutSplitAllocator
 */
export default observer(function PayoutSplitAllocator({
  vm,
}: PayoutSplitAllocatorProps) {
  return (
    <div className={cx(styles.root)}>
      <div>
        {vm.allocations.map((allocation, i) => (
          <AllocationInputView
            key={i}
            allocation={allocation}
            isRemovable={vm.canRemoveAllocation}
          />
        ))}
      </div>
      {vm.allocations.length > 1 && <SplitAssist vm={vm} />}
    </div>
  )
})

interface AllocationInputViewProps {
  allocation: AllocationInputViewModel
  isRemovable: boolean
}

/**
 * Allocation Input View
 */
export const AllocationInputView = observer(function AllocationInputView({
  allocation: vm,
  isRemovable,
}: AllocationInputViewProps) {
  const itemSource = React.useMemo(
    () =>
      (
        searchText: string,
        pagination: CursorPageQuery,
        ct: CancellationToken
      ) =>
        vm.searchMembers(searchText, pagination, ct),
    []
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
    debounce: 300,
  })

  const partialPayoutEnabled = React.useMemo(
    () =>
      getFeatureToggleClient().variation('WorkPortal.PartialPayouts', false),
    []
  )

  return (
    <div className={styles.allocation}>
      <FormRow>
        <FormColumns>
          <FormColumn>
            <FormGroup label="Recipient">
              <CustomSelect
                {...selectProps}
                // Filter out members that are already selected in other allocations.
                searchResults={selectProps.searchResults.filter(
                  (m) => !vm.isAlreadySelected(m)
                )}
                placeholder="Select a member"
                multi={false}
                value={vm.recipient ?? undefined}
                onChange={(m) => vm.setRecipient(m ?? null)}
                itemToString={(v) => String(v?.displayName)}
                data-test="allocation-recipient-selector"
                status={
                  vm.validation.getError('recipient') ? 'danger' : 'default'
                }
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    visual={<Avatar size={24} subject={itemProps.item} />}
                  />
                )}
                renderSearchItem={({ item, itemToString, ...itemProps }) => (
                  <SearchResultItem
                    {...itemProps}
                    visual={<Avatar size={24} subject={item} />}
                  >
                    {item.displayName || `(User ID: ${item.publicId})`}
                  </SearchResultItem>
                )}
              />
              <Help error={vm.validation.getError('recipient')} />
            </FormGroup>
          </FormColumn>
          {isRemovable && (
            <div className={styles.removeBtn}>
              <Button type="text-danger" onClick={vm.remove}>
                Remove allocation
              </Button>
            </div>
          )}
        </FormColumns>
      </FormRow>
      <FormRow>
        <FormColumns>
          <FormColumn>
            <FormGroup label="Amount">
              <NumericInput
                value={vm.allocatedAmount.value}
                currencyCode="usd"
                mode="draft"
                precision={2}
                onChange={(n) => vm.allocatedAmount.setValue(n ?? 0)}
                status={
                  vm.validation.getError('allocatedAmount')
                    ? 'danger'
                    : 'default'
                }
              />
              <Help error={vm.validation.getError('allocatedAmount')} />
            </FormGroup>
          </FormColumn>
          <FormColumn>
            <FormGroup label="Percentage of total">
              <NumericInput
                value={vm.allocatedAmount.percentage}
                mode="live"
                onChange={(n) => vm.allocatedAmount.setPercentage(n ?? 0)}
                status={
                  vm.validation.getError('allocatedAmount')
                    ? 'danger'
                    : 'default'
                }
              />
            </FormGroup>
          </FormColumn>
        </FormColumns>
      </FormRow>
      {partialPayoutEnabled && (
        <FormRow>
          <FormColumns>
            <FormColumn>
              <FormGroup label="Authorized partial payout amount">
                <NumericInput
                  value={vm.authorizedPartialPayoutAmount.value}
                  currencyCode="usd"
                  mode="draft"
                  precision={2}
                  onChange={(n) =>
                    vm.authorizedPartialPayoutAmount.setValue(n ?? 0)
                  }
                  status={
                    vm.validation.getError('authorizedPartialPayoutAmount')
                      ? 'danger'
                      : 'default'
                  }
                />
                <Help
                  error={vm.validation.getError(
                    'authorizedPartialPayoutAmount'
                  )}
                />
              </FormGroup>
            </FormColumn>
            <FormColumn>
              <FormGroup label="Percentage of allocation">
                <NumericInput
                  value={vm.authorizedPartialPayoutAmount.percentage}
                  mode="live"
                  onChange={(n) =>
                    vm.authorizedPartialPayoutAmount.setPercentage(n ?? 0)
                  }
                  status={
                    vm.validation.getError('authorizedPartialPayoutAmount')
                      ? 'danger'
                      : 'default'
                  }
                />
              </FormGroup>
            </FormColumn>
          </FormColumns>
        </FormRow>
      )}
    </div>
  )
})

interface SplitAssistProps {
  vm: PayoutSplitAllocatorViewModel
}

/**
 * Split Assist
 */
export const SplitAssist = observer(function SplitAssist({
  vm,
}: SplitAssistProps) {
  return (
    <div className={styles.splitAssist}>
      <div className={styles.splitAssistMessage}>Need help splitting?</div>
      <div className={styles.splitAssistControls}>
        <Button type="text" onClick={vm.splitEqually}>
          Split equally
        </Button>
        <div className={styles.splitAssistDivider}>|</div>
        <Button
          type="text"
          onClick={vm.fillRemainder}
          disabled={!vm.canFillRemainder}
        >
          Fill remainder
        </Button>
      </div>
    </div>
  )
})

interface TotalAllocatedStatusProps {
  vm: PayoutSplitAllocatorViewModel
}

/**
 * Total Allocated Status
 */
export const TotalAllocatedStatus = observer(function TotalAllocatedStatus({
  vm,
}: TotalAllocatedStatusProps) {
  return (
    <div
      className={cx(
        styles.totalAllocatedStatus,
        vm.allocatedTotal === vm.payoutTotal && styles.exact,
        vm.allocatedTotal > vm.payoutTotal && styles.over
      )}
    >
      <span className={cx(styles.allocatedAmount)}>
        Total allocated: {formatMoney(vm.allocatedTotal)}
      </span>{' '}
      of {formatMoney(vm.payoutTotal)}
    </div>
  )
})

interface AddAllocationButtonProps {
  vm: PayoutSplitAllocatorViewModel
}

/**
 * Add Allocation Button
 */
export const AddAllocationButton = observer(function AddAllocationButton({
  vm,
}: AddAllocationButtonProps) {
  return (
    <Button disabled={!vm.canAddAllocation} onClick={vm.addAllocation}>
      Add Allocation
    </Button>
  )
})
